import { ReactNode } from 'react';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { Shield, Mail, Phone, MapPin, Users, Building, Smartphone, Download } from 'lucide-react';
import logoImage from '@/assets/logo.png';
import playstoreIcon from '/icon-playstore.png';
import appleIcon from '/icon-apple.png';

interface NavigationDrawerProps {
  children: ReactNode;
  title: string;
  type: 'about' | 'contact';
}

export const NavigationDrawer = ({ children, title, type }: NavigationDrawerProps) => {
  const renderContent = () => {
    if (type === 'about') {
      return (
        <div className="space-y-6">
          <div className="space-y-4">
            <h3 className="text-lg font-semibold flex items-center gap-2">
              <img 
                  src={logoImage} 
                  alt="Logo"
                  className="h-8 w-8 h-auto object-cover"
                />
              Sekilas FSP LEM SPSI 
            </h3>
            <p className="text-muted-foreground leading-relaxed">
            Federasi Serikat Pekerja Logam, Elektronik, dan Mesin (FSP LEM) Serikat Pekerja Seluruh Indonesia (SPSI) adalah salah satu organisasi serikat pekerja terkemuka di Indonesia yang bergerak di sektor logam, elektronik, dan mesin.
            </p>
          </div>

          <Separator />

          <div className="space-y-4">
            <h4 className="font-semibold">Tentang fsplemspsi.id</h4>
            <p className="text-muted-foreground">
            Website fsplemspsi.id adalah website Aplikasi Validasi Keanggotaan secara online untuk seluruh anggota FSP LEM SPSI di seluruh Indonesia</p>
            
          </div>
          
          <Separator />

          <div className="space-y-4">
            <h4 className="font-semibold flex items-center gap-2">
              <Smartphone className="h-5 w-5 text-primary" />
              Download Aplikasi Mobile
            </h4>
            <p className="text-muted-foreground">
             Dapatkan aplikasi FSP LEM SPSI Mobile untuk akses cepat pengelolaan keanggotaan oleh admin.
            </p>
            
            <div className="flex flex-col gap-3 mt-4">
              <Button 
                variant="outline" 
                className="w-full justify-start gap-3 h-12"
                onClick={() => window.open('https://play.google.com/store/apps/details?id=org.spsi', '_blank')}
              >
                <div className="flex items-center gap-2">
                  <img src={playstoreIcon} alt="Play Store" className="h-5 w-5" />
                  <div className="text-left">
                    <div className="text-sm font-medium">Download di</div>
                    <div className="text-xs text-muted-foreground">Google Play Store</div>
                  </div>
                </div>
              </Button>
              
              <Button 
                variant="outline" 
                className="w-full justify-start gap-3 h-12"
                onClick={() => window.open('https://apps.apple.com/us/app/fsp-lem-spsi-mobile/id6752684967', '_blank')}
              >
                <div className="flex items-center gap-2">
                  <img src={appleIcon} alt="App Store" className="h-5 w-5" />
                  <div className="text-left">
                    <div className="text-sm font-medium">Download di</div>
                    <div className="text-xs text-muted-foreground">Apple App Store</div>
                  </div>
                </div>
              </Button>
            </div>
          </div>
          
          <Separator />
        </div>
      );
    }

    if (type === 'contact') {
      return (
        <div className="space-y-6">
          <div className="space-y-4">
            <h3 className="text-lg font-semibold flex items-center gap-2">
              <Mail className="h-5 w-5 text-primary" />
              Hubungi Kami
            </h3>
          </div>

          <Separator />

          <div className="space-y-4">
            <div className="flex items-start gap-3">
              <MapPin className="h-5 w-5 text-primary mt-0.5" />
              <div>
                <h4 className="font-semibold mb-1">Kantor DPP FSP LEM SPSI</h4>
                <p className="text-muted-foreground text-sm"
                >Grand Mutiara Platinum No. 2<br />
                Jl Setra Primer Timur, Jakarta Timur
                </p>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <Phone className="h-5 w-5 text-primary mt-0.5" />
              <div>
                <h4 className="font-semibold mb-1">Telepon</h4>
                <p className="text-muted-foreground text-sm">
                  +62 (021)22859565
                  <br />
                  Senin - Jumat: 08.00 - 17.00 WIB
                </p>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <Mail className="h-5 w-5 text-primary mt-0.5" />
              <div>
                <h4 className="font-semibold mb-1">Email</h4>
                <p className="text-muted-foreground text-sm">
                redaksifsplem@gmail.com
                </p>
              </div>
            </div>
          </div>

          <Separator />
        </div>
      );
    }
  };

  return (
    <Sheet>
      <SheetTrigger asChild>
        {children}
      </SheetTrigger>
      <SheetContent side="right" className="w-[400px] sm:w-[540px] flex flex-col">
        <SheetHeader className="flex-shrink-0">
          <SheetTitle className="flex items-center gap-2">
            <Shield className="h-5 w-5 text-primary" />
            {title}
          </SheetTitle>
          <SheetDescription>
            {type === 'about' 
              ? (
                <>
                  Pelajari lebih lanjut tentang organisasi kami di{' '}
                  <a 
                    href="https://fsplemspsi.or.id" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-primary hover:underline"
                  >
                    https://fsplemspsi.or.id
                  </a>
                </>
              )
              : 'Untuk informasi lebih lanjut mengenai FSP LEM SPSI, silakan hubungi kami melalui kontak berikut'
            }
          </SheetDescription>
        </SheetHeader>
        <div className="mt-6 flex-1 overflow-y-auto">
          {renderContent()}
        </div>
      </SheetContent>
    </Sheet>
  );
};